import { useMemo } from "react";
import { apiService } from "../../services/api";

// Partage d'une version avec le client (portail client du projet) depuis le panneau Versions d'une présentation ou d'un
// rapport. Seulement pour un document rattaché à un projet, et pour les administrateurs (comme dans l'onglet du
// projet). Le client voit toujours une seule version : en partager une autre remplace la précédente.
const isAdmin = (user) => Boolean(user?.is_admin) || Number(user?.employee_id ?? user?.id) === 1;

// Partager sort le document de l'entreprise : la confirmation dit chez qui il part, qui le verra, et que celui qui
// partage répond de son contenu (Rémy, 29/10). Même texte depuis l'éditeur et depuis l'onglet Portail du projet.
export const confirmClientShare = ({ title, number, replacedNumber, clientName, invited = [] }) => {
  const who = invited.length
    ? `Il sera visible sur le portail client par : ${invited.slice(0, 6).join(", ")}${invited.length > 6 ? ` et ${invited.length - 6} autre(s)` : ""}.`
    : "Personne n'est encore invité sur le portail : il sera visible dès la première invitation.";
  return window.confirm([
    `Partager « ${title} » v${number} avec le client${clientName ? ` ${clientName}` : ""} ?`,
    "",
    who,
    replacedNumber ? `Il remplacera la v${replacedNumber} partagée aujourd'hui.` : null,
    "",
    "Vous êtes responsable de ce que vous partagez : vérifiez qu'il ne contient rien d'interne (prix, marges, "
      + "notes, informations d'un autre client). Les slides et sections masquées ne sont pas envoyées.",
  ].filter((line) => line !== null).join("\n"));
};

const useClientShare = ({ data, setData, user, refreshRevisions, setMessage, revisions }) => {
  const portal = data?.client_portal;
  return useMemo(() => {
    if (!portal) return null;
    const base = `/projects/${portal.project_id}/portal/shares`;
    const after = async (share, message) => {
      setData((previous) => ({ ...previous, client_portal: { ...previous.client_portal, share } }));
      await refreshRevisions?.();
      setMessage?.(message);
    };
    return {
      canShare: isAdmin(user),
      share: portal.share,
      onShare: async (revision) => {
        const replaced = portal.share && portal.share.revision_id !== revision.id;
        const replacedNumber = replaced ? revisions?.find((r) => r.id === portal.share.revision_id)?.number || "précédente" : null;
        if (!confirmClientShare({ title: data.presentation.title, number: revision.number, replacedNumber,
          clientName: portal.client_name, invited: portal.invited })) return;
        try {
          const result = await apiService.post(base, { presentation_id: data.presentation.id, revision_id: revision.id });
          await after({ id: result.id, revision_id: revision.id, published_at: new Date().toISOString() },
            `v${revision.number} partagée avec le client : figée, elle ne bougera plus.`);
        } catch (error) {
          setMessage?.(error.message);
        }
      },
      onWithdraw: async () => {
        if (!portal.share || !window.confirm("Le client ne verra plus ce document. Continuer ?")) return;
        try {
          await apiService.delete(`${base}/${portal.share.id}`);
          await after(null, "Document retiré du portail client.");
        } catch (error) {
          setMessage?.(error.message);
        }
      },
    };
  }, [portal, data?.presentation?.id, data?.presentation?.title, user, setData, refreshRevisions, setMessage, revisions]);
};

export default useClientShare;
